import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Button, Flex, Typography } from 'antd';
import { Document as PdfDocument, Page, pdfjs } from 'react-pdf';

/**
 * The recommended `new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url)`
 * approach breaks under CRA/webpack 5: the file is transpiled by the
 * `babel-preset-react-app/dependencies` rule (to polyfill the private class
 * fields pdfjs-dist v5 uses), which injects `@babel/runtime` helper imports
 * pointing at the machine's absolute node_modules path. Since the worker is
 * emitted as a raw asset (not bundled), those imports are never rewritten and
 * 404 in production, which nginx's SPA fallback then serves as `text/html`,
 * breaking the worker. Serving a static copy from `public/` bypasses the
 * webpack/babel pipeline entirely.
 */
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

/**
 * Renders a PDF document with navigation buttons for paging through the document.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.currentFile - The current file object containing the blob of the PDF document.
 * @param {number} props.pageNumber - The current page number.
 * @param {Function} props.setPageNumber - The function to update the current page number.
 * @returns {JSX.Element} The rendered PDF document component.
 */
export const PDFviewer = ({ currentFile, pageNumber, setPageNumber }) => {
  const { t } = useTranslation();
  const [numOfPages, setNumOfPages] = useState();
  const isLastPage = pageNumber === numOfPages;
  const isFirstPage = pageNumber === 1;

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumOfPages(numPages);
  };

  const nextPage = () => {
    const nextPageNumber = pageNumber + 1;
    if (nextPageNumber <= numOfPages) {
      setPageNumber(nextPageNumber);
    }
    globalThis.scrollTo(0, 0);
  };

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <PdfDocument
        file={currentFile}
        onLoadSuccess={onDocumentLoadSuccess}
        className="react-pdf__Document"
      >
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Page
            scale={1.3}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            pageNumber={pageNumber}
          />
        </div>
        <Flex gap="middle" align="center" justify="center">
          <Button
            disabled={isFirstPage}
            onClick={() => setPageNumber(pageNumber - 1)}
          >
            {t('buttons.previous')}
          </Button>
          <Typography>{`${pageNumber} / ${numOfPages}`}</Typography>
          <Button
            onClick={() => nextPage()}
            type={isLastPage ? 'primary' : 'default'}
          >
            {t('buttons.next')}
          </Button>
        </Flex>
      </PdfDocument>
    </div>
  );
};

PDFviewer.propTypes = {
  currentFile: PropTypes.shape({
    url: PropTypes.string
  }),
  pageNumber: PropTypes.number,
  setPageNumber: PropTypes.func
};

PDFviewer.defaultProps = {
  currentFile: {},
  pageNumber: 1,
  setPageNumber: () => {}
};
